import { shopRequest } from "../components/shop/shopRequest";
import { createHttpOwnerListsApi } from "./ownerListsApi";
import { requestTimeout } from "../lib/requestTimeout";
import { createHttpOrdersApi } from "./ordersApi";
import { createHttpCommerceApi } from "./commerceApi";
import { z, type ZodType } from "zod";
import {
  ActionEnvelopeSchema,
  AssetRefSchema,
  ImageLookSchema,
  PublishingStatusSchema,
  SiteDomainSchema,
  type DomainProblem,
  type AddressProblem,
  CommentListSchema,
  ContentLibrarySchema,
  ConversationSchema,
  WorkspaceSchema,
  type ActionSummary,
  type BuilderApi,
  type PublicationRequest,
  type OwnerActionRequest,
  type SendMessageRequest,
  type NewComment,
  type BlogPostEntry,
  ShopIndexSchema, ShopProductEntrySchema, ShopStockSchema, ShopFileSchema, ShopHistorySchema, ShopProductRevisionSchema, type ShopApi,
} from "./contract";
import { BlogRefusedError, ShopRefusedError, BuilderRequestError, failureForStatus } from "./errors";

type Fetch = typeof fetch;

const segment = (value: string) => encodeURIComponent(value);

const PostCopyShape = z.object({ title: z.string(), slug: z.string(), summary: z.string().optional(), cover: z.object({ assetId: z.string(), version: z.number().int(), alt: z.string() }).optional(), tags: z.array(z.string()), revision: z.string(), publishAt: z.string().optional() });
const BlogPostShape = z.object({
  id: z.string(), working: PostCopyShape, live: PostCopyShape.nullable(), status: z.enum(["draft", "scheduled", "published"]), hasChanges: z.boolean(),
  firstPublishedAt: z.string().nullable(), updatedAt: z.string().optional(), rowVersion: z.number().int(), undoable: z.boolean(), redoable: z.boolean().default(false), coverSrc: z.string().optional(),
});
const BlogIndexShape = z.object({
  total: z.number().optional(), counts: z.object({ all: z.number(), draft: z.number(), scheduled: z.number(), published: z.number() }).optional(), tagCounts: z.record(z.string(), z.number()).optional(),
  settings: z.object({ name: z.string(), base: z.string(), enabled: z.boolean(), addToMenu: z.boolean(), latestOnHome: z.boolean() }).nullable(),
  version: z.number().int(),
  posts: z.array(BlogPostShape),
  tags: z.array(z.object({ id: z.string(), label: z.string(), slug: z.string() })),
  photos: z.array(z.object({ id: z.string(), src: z.string(), alt: z.string() })),
});

/** Same-origin client for `/_builder`. Session cookies authenticate every call. */
export function createHttpBuilderApi(fetcher: Fetch = (...args) => fetch(...args)): BuilderApi {
  const fetchImpl: Fetch = (input, init) => requestTimeout(signal => fetcher(input, { ...init, signal }), init?.signal ?? undefined, 60_000);
  async function request<T>(
    path: string,
    schema: ZodType<T, z.ZodTypeDef, unknown>,
    init: RequestInit & { okStatuses?: number[] } = {},
  ): Promise<{ status: number; body: T }> {
    const { okStatuses = [200], ...rest } = init;
    let response: Response;
    try {
      response = await fetchImpl(path, {
        credentials: "same-origin",
        cache: "no-store",
        ...rest,
        headers: { Accept: "application/json", ...(rest.body ? { "Content-Type": "application/json" } : {}) },
      });
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") throw caught;
      throw new BuilderRequestError("offline", null);
    }
    if (!okStatuses.includes(response.status)) throw new BuilderRequestError(failureForStatus(response.status), response.status);
    let parsed: unknown;
    try {
      parsed = await response.json();
    } catch {
      throw new BuilderRequestError("unavailable", response.status);
    }
    const result = schema.safeParse(parsed);
    if (!result.success) throw new BuilderRequestError("unavailable", response.status);
    return { status: response.status, body: result.data };
  }

  const post = (body: unknown): RequestInit => ({ method: "POST", body: JSON.stringify(body) });
  /** The blog's writes answer 400 with the reason in words, and 409 for a post changed somewhere else. */
  async function blogWrite<T>(path: string, schema: ZodType<T, z.ZodTypeDef, unknown>, body: unknown): Promise<T> {
    let response: Response;
    try {
      response = await fetchImpl(path, { credentials: "same-origin", cache: "no-store", ...post(body), headers: { Accept: "application/json", "Content-Type": "application/json" } });
    } catch {
      throw new BuilderRequestError("offline", null);
    }
    if (response.status === 400) {
      const refused = z.object({ error: z.literal("refused"), message: z.string() }).safeParse(await response.json().catch(() => null));
      if (refused.success) throw new BlogRefusedError(refused.data.message);
    }
    if (response.status !== 200) throw new BuilderRequestError(failureForStatus(response.status), response.status);
    const parsed = schema.safeParse(await response.json().catch(() => null));
    if (!parsed.success) throw new BuilderRequestError("unavailable", response.status);
    return parsed.data;
  }
  /** Domain requests answer 409 with the problem the owner can fix. */
  async function domainCall<T>(path: string, schema: ZodType<T, z.ZodTypeDef, unknown>, init: RequestInit): Promise<T | { problem: DomainProblem }> {
    const { status, body } = await request(path, z.union([schema, z.object({ problem: z.string() })]), { ...init, okStatuses: [200, 409] });
    return status === 409 ? body as { problem: DomainProblem } : body as T;
  }

  return {
    shop: createHttpShopApi(fetcher),
    lists: createHttpOwnerListsApi(fetcher),
    async getWorkspace(siteId, signal) {
      return (await request(`/_builder/sites/${segment(siteId)}/workspace`, WorkspaceSchema, { signal })).body;
    },
    async getAction(siteId, actionId, signal) {
      const { body } = await request(`/_builder/sites/${segment(siteId)}/actions/${segment(actionId)}`, ActionEnvelopeSchema, { signal });
      return body.action;
    },
    async preparePublication(siteId, input: PublicationRequest): Promise<ActionSummary> {
      const { body } = await request(`/_builder/sites/${segment(siteId)}/publications`, ActionEnvelopeSchema, post(input));
      return body.action;
    },
    async confirmPublication(siteId, actionId, proposalDigest) {
      const { body } = await request(
        `/_builder/sites/${segment(siteId)}/publications/${segment(actionId)}/confirm`,
        ActionEnvelopeSchema,
        post({ proposalDigest }),
      );
      return body.action;
    },
    async getConversation(siteId, signal) {
      return (await request(`/_builder/sites/${segment(siteId)}/conversation`, ConversationSchema, { signal })).body;
    },
    async sendMessage(siteId, input: SendMessageRequest) {
      const body = { messageId: input.messageId, text: input.text, ...(input.pagePath ? { pagePath: input.pagePath } : {}), ...(input.editorContext ? { editorContext: input.editorContext } : {}), ...(input.attachments?.length ? { attachments: input.attachments } : {}), ...(input.post ? { post: input.post } : {}), ...(input.openPost ? { openPost: input.openPost } : {}) };
      return (await request(`/_builder/sites/${segment(siteId)}/conversation`, ConversationSchema, { ...post(body), okStatuses: [200, 202] })).body;
    },
    async decideConfirmation(siteId, decision) {
      return (await request(`/_builder/sites/${segment(siteId)}/conversation/confirmations`, ConversationSchema, post({ turnId: decision.turnId, confirmationId: decision.confirmationId, decision: decision.decision, ...(decision.alternativeId !== undefined ? { alternativeId: decision.alternativeId } : {}) }))).body;
    },
    async decideProductSuggestion(siteId, decision) {
      return (await request(`/_builder/sites/${segment(siteId)}/conversation/product-decisions`, ConversationSchema, post(decision))).body;
    },
    async decidePostRewrite(siteId, decision) {
      return (await request(`/_builder/sites/${segment(siteId)}/conversation/post-decisions`, ConversationSchema, post({ turnId: decision.turnId, decision: decision.decision, ...(decision.index !== undefined ? { index: decision.index } : {}) }))).body;
    },
    async confirmAction(siteId, actionId, proposalDigest) {
      const { body } = await request(
        `/_builder/sites/${segment(siteId)}/actions/${segment(actionId)}/confirm`,
        ActionEnvelopeSchema,
        post({ proposalDigest }),
      );
      return body.action;
    },
    async getPublishing(siteId, signal) {
      const { body } = await request(`/_builder/sites/${segment(siteId)}/publishing`, PublishingStatusSchema, { signal });
      return body;
    },
    async changeAddress(siteId, subdomain) {
      const { status, body } = await request(
        `/_builder/sites/${segment(siteId)}/address`,
        z.union([z.object({ origin: z.string() }), z.object({ problem: z.enum(["invalid", "reserved", "taken", "live"]) })]),
        { ...post({ subdomain }), okStatuses: [200, 409] },
      );
      return status === 409 ? body as { problem: AddressProblem } : body;
    },
    async addDomain(siteId, hostname) {
      return domainCall(`/_builder/sites/${segment(siteId)}/domain`, SiteDomainSchema, post({ hostname }));
    },
    async checkDomain(siteId) {
      return domainCall(`/_builder/sites/${segment(siteId)}/domain/check`, SiteDomainSchema, post({}));
    },
    async switchAddress(siteId, to) {
      return domainCall(`/_builder/sites/${segment(siteId)}/domain/switch`, z.object({ origin: z.string() }), post({ to }));
    },
    async removeDomain(siteId) {
      return domainCall(`/_builder/sites/${segment(siteId)}/domain`, z.object({ removed: z.literal(true) }), { method: "DELETE", body: "{}" });
    },
    async startCheckout(siteId, cycle) {
      const { body } = await request(`/_builder/sites/${segment(siteId)}/checkout`, z.object({ url: z.string().url() }), post({ cycle }));
      return body.url;
    },
    async uploadPhoto(siteId, photo) {
      let response: Response;
      try {
        response = await fetchImpl(`/_builder/sites/${segment(siteId)}/uploads`, {
          method: "POST", credentials: "same-origin", cache: "no-store",
          headers: { Accept: "application/json", "Content-Type": photo.type },
          body: photo,
        });
      } catch {
        throw new BuilderRequestError("offline", null);
      }
      if (response.status !== 200) throw new BuilderRequestError(failureForStatus(response.status), response.status);
      return z.object({ asset: AssetRefSchema }).parse(await response.json()).asset;
    },
    async addPostPhoto(siteId, source) {
      const { body } = await request(`/_builder/sites/${segment(siteId)}/post-photos`, z.object({ photo: z.object({ assetId: z.string(), version: z.number().int(), src: z.string(), alt: z.string().optional() }) }), post(source));
      return body.photo;
    },
    async describePhoto(siteId, asset, purpose) {
      const { body } = await request(`/_builder/sites/${segment(siteId)}/photos/look`, z.object({ look: ImageLookSchema }), post({ asset, ...(purpose === "reference" ? { purpose } : {}) }));
      return body.look;
    },
    async getContent(siteId, signal) {
      const { body } = await request(`/_builder/sites/${segment(siteId)}/content`, ContentLibrarySchema, { signal });
      return body;
    },
    async getBlog(siteId, signal) {
      return (await request(`/_builder/sites/${segment(siteId)}/blog?view=owner`, BlogIndexShape, { signal })).body;
    },
    async saveBlogSettings(siteId, input) {
      return blogWrite(`/_builder/sites/${segment(siteId)}/blog/settings?view=owner`, BlogIndexShape, input);
    },
    async createBlogTag(siteId, input) {
      return blogWrite(`/_builder/sites/${segment(siteId)}/blog/tags?view=owner`, BlogIndexShape, input);
    },
    async updateBlogTag(siteId, id, input) {
      return blogWrite(`/_builder/sites/${segment(siteId)}/blog/tags/${segment(id)}?view=owner`, BlogIndexShape, input);
    },
    async deleteBlogTag(siteId, id) {
      return blogWrite(`/_builder/sites/${segment(siteId)}/blog/tags/${segment(id)}/delete?view=owner`, BlogIndexShape, {});
    },
    async createPost(siteId, input) {
      return (await blogWrite(`/_builder/sites/${segment(siteId)}/blog/posts`, z.object({ post: BlogPostShape }), input)).post as BlogPostEntry;
    },
    async savePost(siteId, id, rowVersion, set) {
      return (await blogWrite(`/_builder/sites/${segment(siteId)}/blog/posts/${segment(id)}`, z.object({ post: BlogPostShape }), { rowVersion, set })).post as BlogPostEntry;
    },
    async postAction(siteId, id, action, rowVersion) {
      const done = await blogWrite(`/_builder/sites/${segment(siteId)}/blog/posts/${segment(id)}/${action}`, z.union([z.object({ post: BlogPostShape }), z.object({ ok: z.literal(true) })]), { rowVersion });
      return "post" in done ? done.post as BlogPostEntry : null;
    },
    async savePostRevision(siteId, input) {
      return (await request(`/_builder/sites/${segment(siteId)}/post-revisions`, z.object({ digest: z.string(), wordCount: z.number().int() }), post(input))).body;
    },
    async getPostRevision(siteId, digest, signal) {
      return (await request(`/_builder/sites/${segment(siteId)}/post-revisions/${segment(digest)}`, z.object({ revision: z.object({ bodyMd: z.string(), seo: z.object({ title: z.string().optional(), description: z.string().optional(), imageCrop: z.object({ x: z.number(), y: z.number(), width: z.number(), height: z.number() }).optional() }), wordCount: z.number().int(), photos: z.array(z.object({ assetId: z.string(), version: z.number().int(), src: z.string() })).optional() }) }), { signal })).body.revision;
    },
    async submitAction(siteId, input: OwnerActionRequest) {
      const { body } = await request(`/_builder/sites/${segment(siteId)}/actions`, ActionEnvelopeSchema, post(input));
      return body.action;
    },
    async listComments(siteId, signal) {
      return (await request(`/_builder/sites/${segment(siteId)}/comments`, CommentListSchema, { signal })).body.comments;
    },
    async createComment(siteId, comment: NewComment) {
      return (await request(`/_builder/sites/${segment(siteId)}/comments`, CommentListSchema, post(comment))).body.comments;
    },
    async updateComment(siteId, commentId, note) {
      return (await request(`/_builder/sites/${segment(siteId)}/comments/${segment(commentId)}`, CommentListSchema, { method: "PATCH", body: JSON.stringify({ note }) })).body.comments;
    },
    async deleteComment(siteId, commentId) {
      return (await request(`/_builder/sites/${segment(siteId)}/comments/${segment(commentId)}`, CommentListSchema, { method: "DELETE" })).body.comments;
    },
    async sendComments(siteId, input) {
      const { status, body } = await request(
        `/_builder/sites/${segment(siteId)}/comments/send`,
        z.union([ConversationSchema, z.object({ error: z.string(), ids: z.array(z.string()).optional() })]),
        { ...post(input), okStatuses: [200, 202, 409] },
      );
      if (status !== 409) return body as z.infer<typeof ConversationSchema>;
      const refused = body as { error: string; ids?: string[] };
      if (refused.error === "moved") return { moved: refused.ids ?? [] };
      throw new BuilderRequestError("rejected", 409);
    },
    async cancelAction(siteId, actionId) {
      const { status, body } = await request(
        `/_builder/sites/${segment(siteId)}/actions/${segment(actionId)}/cancel`,
        ActionEnvelopeSchema,
        { ...post({}), okStatuses: [200, 409] },
      );
      return { action: body.action, cancelled: status === 200 && body.action.state === "cancelled" };
    },
  };
}

/** Owner-only catalogue client, also usable independently of the Website workspace. */
export function createHttpShopApi(fetchImpl: Fetch = (...args) => fetch(...args)): ShopApi {
  const root = (siteId: string) => `/_builder/sites/${segment(siteId)}/shop`;
  const productPath = (siteId: string, id: string) => `${root(siteId)}/products/${segment(id)}`;
  async function call<T>(path: string, schema: ZodType<T, z.ZodTypeDef, unknown>, body?: unknown, signal?: AbortSignal, extraHeaders?: Record<string, string>): Promise<T> {
    return shopRequest(async requestSignal => {
    let response: Response;
    try {
      response = await fetchImpl(path, { credentials: "same-origin", cache: "no-store", signal: requestSignal, headers: { Accept: "application/json", ...(body !== undefined ? { "Content-Type": body instanceof Blob ? body.type || "application/octet-stream" : "application/json" } : {}), ...extraHeaders }, ...(body !== undefined ? { method: "POST", body: body instanceof Blob ? body : JSON.stringify(body) } : {}) });
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") throw caught;
      throw new BuilderRequestError("offline", null);
    }
    const data: unknown = await response.json().catch(() => null);
    if (response.status === 400) {
      const refused = z.object({ error: z.literal("refused"), message: z.string() }).safeParse(data);
      if (refused.success) throw new ShopRefusedError(refused.data.message);
    }
    if (response.status !== 200) throw new BuilderRequestError(failureForStatus(response.status), response.status);
    const parsed = schema.safeParse(data);
    if (!parsed.success) throw new BuilderRequestError("unavailable", response.status);
    return parsed.data;
    }, signal, body instanceof Blob ? 120_000 : 30_000);
  }
  const uploads = new WeakMap<Blob, { key: string; id: string; chunkSize: number; nextPart: number }>();
  const product = async (siteId: string, id: string, suffix: string, body: unknown) => (await call(`${productPath(siteId, id)}${suffix}`, z.object({ product: ShopProductEntrySchema }), body)).product;
  return {
    commerce: createHttpCommerceApi(fetchImpl),
    lists: createHttpOwnerListsApi(fetchImpl),
    orders: createHttpOrdersApi(fetchImpl),
    getShop: (siteId, signal) => call(`${root(siteId)}?view=owner`, ShopIndexSchema, undefined, signal),
    saveShopSettings: (siteId, settings, rowVersion) => call(`${root(siteId)}/settings?view=owner`, ShopIndexSchema, { settings, rowVersion }),
    createProduct: async (siteId, input) => (await call(`${root(siteId)}/products`, z.object({ product: ShopProductEntrySchema }), input)).product,
    saveProduct: (siteId, id, rowVersion, set) => product(siteId, id, "", { rowVersion, set }),
    saveProductOptions: (siteId, id, rowVersion, options) => product(siteId, id, "/options", { rowVersion, options }),
    saveProductVariants: (siteId, id, rowVersion, variants) => product(siteId, id, "/variants", { rowVersion, variants }),
    setProductAvailability: (siteId, id, rowVersion, availability) => product(siteId, id, "/availability", { rowVersion, availability }),
    deleteProduct: async (siteId, id, rowVersion) => { await call(`${productPath(siteId, id)}/delete`, z.object({ ok: z.literal(true) }), { rowVersion }); },
    undoProduct: (siteId, id, rowVersion) => product(siteId, id, "/undo", { rowVersion }),
    restoreProduct: (siteId, id, rowVersion, seq) => product(siteId, id, "/restore", { rowVersion, seq }),
    getProductHistory: async (siteId, id, signal) => (await call(`${productPath(siteId, id)}/history`, z.object({ history: ShopHistorySchema }), undefined, signal)).history,
    saveProductRevision: (siteId, input) => call(`${root(siteId)}/revisions`, z.object({ digest: z.string() }), input),
    getProductRevision: async (siteId, hash, signal) => (await call(`${root(siteId)}/revisions/${segment(hash)}`, z.object({ revision: ShopProductRevisionSchema }), undefined, signal)).revision,
    saveVariantStock: async (siteId, variantId, rowVersion, stock) => (await call(`${root(siteId)}/stock/${segment(variantId)}`, z.object({ stock: ShopStockSchema }), { rowVersion, stock })).stock,
    saveShopCategory: (siteId, id, rowVersion, set) => call(`${root(siteId)}/categories/${segment(id)}?view=owner`, ShopIndexSchema, { rowVersion, set }),
    deleteShopCategory: (siteId, id, rowVersion) => call(`${root(siteId)}/categories/${segment(id)}/delete?view=owner`, ShopIndexSchema, { rowVersion }),
    saveCustomizationSet: (siteId, id, rowVersion, set) => call(`${root(siteId)}/customization-sets/${segment(id)}?view=owner`, ShopIndexSchema, { rowVersion, set }),
    deleteCustomizationSet: (siteId, id, rowVersion) => call(`${root(siteId)}/customization-sets/${segment(id)}/delete?view=owner`, ShopIndexSchema, { rowVersion }),
    registerProductPhoto: async (siteId, id, input) => (await call(`${productPath(siteId, id)}/photos`, z.object({ photo: z.object({ assetId: z.string(), version: z.number().int(), src: z.string(), alt: z.string().optional() }) }), input)).photo,
    uploadProductFile: async (siteId, id, fileId, name, body, options) => {
      if (!body.size || body.size > 2 * 1024 * 1024 * 1024) throw new ShopRefusedError("Choose a file up to 2 GB");
      const root = `${productPath(siteId, id)}/uploads`;
      const key = `${root}/${fileId}`;
      const signal = options?.signal;
      if (signal?.aborted) throw new DOMException("Upload cancelled", "AbortError");
      let upload = uploads.get(body);
      if (!upload || upload.key !== key) {
        const response = await call(root, z.object({ upload: z.object({ id: z.string(), chunkSize: z.number().int().positive().max(8 * 1024 * 1024) }) }), { id: fileId, name, contentType: body.type || "application/octet-stream", size: body.size }, signal);
        upload = { ...response.upload, key, nextPart: 1 }; uploads.set(body, upload);
      }
      const session = upload;
      const cancel = () => { uploads.delete(body); void call(`${root}/${segment(session.id)}/cancel`, z.object({ ok: z.literal(true) }), {}).catch(() => undefined); };
      signal?.addEventListener("abort", cancel, { once: true });
      if (signal?.aborted) { cancel(); throw new DOMException("Upload cancelled", "AbortError"); }
      try {
        options?.onProgress?.(Math.floor(Math.min(body.size, (session.nextPart - 1) * session.chunkSize) / body.size * 99));
        const count = Math.ceil(body.size / session.chunkSize);
        for (; session.nextPart <= count; session.nextPart++) {
          const start = (session.nextPart - 1) * session.chunkSize;
          await call(`${root}/${segment(session.id)}/parts/${session.nextPart}`, z.object({ part: z.object({ partNumber: z.number().int(), etag: z.string() }) }), body.slice(start, Math.min(body.size, start + session.chunkSize)), signal);
          options?.onProgress?.(Math.floor(Math.min(body.size, start + session.chunkSize) / body.size * 99));
        }
        const result = await call(`${root}/${segment(session.id)}/complete`, z.object({ file: ShopFileSchema }), {}, signal);
        uploads.delete(body); options?.onProgress?.(100); return result.file;
      } finally { signal?.removeEventListener("abort", cancel); }
    },
    registerProductFile: async (siteId, id, input) => (await call(`${productPath(siteId, id)}/files`, z.object({ file: ShopFileSchema }), input)).file,
  };
}
